<script lang="ts">
  import { onMount } from 'svelte';
  import Button from '$lib/components/common/button/Button.svelte';
  import { showToast } from '$lib/helpers/toastrHelper';
  import { fetchWithAuth } from '$lib/utils/http/httpUtil';
  import { Info, Warning, Eye, EyeSlash } from 'phosphor-svelte';
  import RequiredBadge from '$lib/components/common/badges/RequiredBadge.svelte';
  import { getToastStore, getModalStore } from '@skeletonlabs/skeleton';
  import type { ModalSettings, ModalComponent } from '@skeletonlabs/skeleton';

  export let clientId: string = '';
  export let clientName: string = '';

  const toastrStore = getToastStore();
  const modalStore = getModalStore();

  // Form state management
  let isLoading = false;
  let isExecuting = false;
  let hasExistingConfig = false;
  
  // Id visibility toggles
  let showClientId = false;
  let showTenantId = false;

  // Sharepoint setup parameters
  let sharepointClientId = '';
  let tenantId = '';
  let siteUrl = '';
  let folderLocation = '';
  let folderUrl = '';
  let hasCertificate = false;
  let hasCertificateText = '';
  
  // Single function to update form data from server response
  function updateFormFromConfig(config: any) {
    sharepointClientId = config.sharepointClientId || '';
    tenantId = config.tenantId || '';
    siteUrl = config.siteUrl || '';
    folderLocation = config.sharepointFolderLocation || '';
    folderUrl = config.sharepointFolderUrl || '';
    hasCertificate = !!config.hasCertificateFile;
    hasCertificateText = !!config.hasCertificateFile ? 'Certificate has been loaded' : '';
  }

  function clearForm() {
    sharepointClientId = '';
    tenantId = '';
    siteUrl = '';
    folderLocation = '';
    folderUrl = '';
  }

  $: formIsValid = !!(clientId && sharepointClientId && tenantId && siteUrl && folderLocation && folderUrl);
  $: canSave = formIsValid && !isExecuting;

  onMount(async () => {
    if (clientId) {
      await loadExistingSharepointConfig();
    }
  });

  async function loadExistingSharepointConfig() {
    if (!clientId || isLoading) return;

    isLoading = true;
    hasExistingConfig = false;

    try {
      const response = await fetchWithAuth(`/api/export/sharepoint-config/${clientId}`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data.exists) {
          hasExistingConfig = true;
          updateFormFromConfig(result.data.config);
        } else {
          hasExistingConfig = false;
        }
      }
    } catch (error) {
      console.error('Failed to check existing Sharepoint config:', error);
      hasExistingConfig = false;
    } finally {
      isLoading = false;
    }
  }
  
  // Common fetch error handler
  function handleApiError(error: any, message: string) {
    console.error(`${message}:`, error);
    showToast({
      title: 'Error',
      message: error?.message || message,
      type: 'error',
    });
  }

  function createRequestBody() {
    return {
      clientId: parseInt(clientId),
      sharepointClientId,
      tenantId,
      siteUrl,
      folderLocation,
      folderUrl
    };
  }
  async function saveSharepointConfiguration() {
    if (!canSave) return;
    
    isExecuting = true;

    try {
      const requestBody = createRequestBody();

      const response = await fetchWithAuth('/api/export/sharepoint-setup', {
        method: 'POST',
        body: JSON.stringify(requestBody),
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();

      if (result.success) {
        hasExistingConfig = true;
        showToast({
          title: 'Success',
          message: 'Sharepoint configuration saved successfully',
          type: 'success',
        });

      } else {
        showToast({
          title: 'Error',
          message: 'Failed to save Sharepoint configuration',
          type: 'error',
        });

        let errorMessage = '';
        
        if (Array.isArray(result.errors)) {
          errorMessage = result.errors.join(', ');
        } else if (typeof result.errors === 'string') {
          errorMessage = result.errors;
        } else if (result.errors) {
          errorMessage = JSON.stringify(result.errors);
        }
        
        if (errorMessage) {
          showToast({
            title: 'Error',
            message: errorMessage,
            type: 'error',
          });
        }
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to save Sharepoint configuration';
      
      handleApiError(error, 'Failed to save Sharepoint configuration');
    } finally {
      isExecuting = false;
    }
  }
  
  async function updateCertificate(certificateDetails) {
    isExecuting = true;
    try {
      const response = await fetchWithAuth('/api/export/sharepoint-certificate', {
        method: 'POST',
        body: JSON.stringify(certificateDetails),
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();

      if (result.success) {
        await loadExistingSharepointConfig();
        showToast({
          title: 'Success',
          message: 'Certificate updated successfully',
          type: 'success',
        });
      } else {
        showToast({
          title: 'Error',
          message: 'Failed to update certificate',
          type: 'error',
        });
      }
    } catch (error) {
      handleApiError(error, 'Failed to update certificate');
    } finally {
      isExecuting = false;
    }
  }

  function showCertificateModal() {
    modalStore.trigger({
      type: 'component',
      component: 'sharepointV3CertifcateModal',
      meta: {
        onConfirm: (certificateFile, certificatePassword) => updateCertificate(certificateFile, certificatePassword),
        clientName: clientName,
        clientId: clientId,
      },
    });
  }
  
  async function retrieveMetaData() {
    try {
      const response = await fetchWithAuth(`/api/export/sharepoint-retrieve-export-metadata?clientId=${clientId}`);

      const result = await response.json();

      if (result.success) {
        // Show modal with response
        modalStore.trigger({
          type: 'component',
          component: 'exportMetaDataModal',
          title: `Exported Metadata Columns`,
          meta: {
            success: true,
            responseData: { data: result.data },
            title: `Exported Metadata Columns`
          }
        });
      } else {
        // Show modal with error
        modalStore.trigger({
          type: 'component',
          component: 'exportMetaDataModal',
          title: `Exported Metadata Columns`,
          meta: {
            success: false,
            responseData: { error: result.error || 'Failed to retrieve Exported Metadata Columns' },
            title: `Exported Metadata Columns`
          }
        });
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to retrieve Exported Metadata Columns';
      
      // Show modal with error
      modalStore.trigger({
        type: 'component',
        component: 'exportMetaDataModal',
        title: `Exported Metadata Columns`,
        meta: {
          success: false,
          responseData: { error: errorMessage },
          title: `Exported Metadata Columns`
        }
      });
    }
  }

  async function retrieveFileList() {
    try {
      const response = await fetchWithAuth(`/api/export/sharepoint-retrieve-export-file-list?clientId=${clientId}`);

      const result = await response.json();

      if (result.success) {
        // Show modal with response
        modalStore.trigger({
          type: 'component',
          component: 'exportFileListModal',
          title: `Exported Recent File List`,
          files: {
            success: true,
            responseData: { data: result.data },
            title: `Exported Recent File List`
          }
        });
      } else {
        // Show modal with error
        modalStore.trigger({
          type: 'component',
          component: 'exportFileListModal',
          title: `Exported Recent File List`,
          files: {
            success: false,
            responseData: { error: result.error || 'Failed to retrieve Exported Recent File List' },
            title: `Exported Recent File List`
          }
        });
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to retrieve Exported Recent File List';
      
      // Show modal with error
      modalStore.trigger({
        type: 'component',
        component: 'exportFileListModal',
        title: `Exported Recent File List`,
        files: {
          success: false,
          responseData: { error: errorMessage },
          title: `Exported Recent File List`
        }
      });
    }
  }

  async function getExportStatus() {
    try {
      const response = await fetchWithAuth(`/api/export/pending-exports/${clientId}`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data.summary) {
          showToast({
            title: 'Invoice Export Status',
            message: result.data.summary,
            type: 'info',
          });
        } else {
          showToast({
            title: 'Error',
            message: 'Failed to retrieve invoice export status',
            type: 'error',
          });
        }
      }
    } catch (error) {
      console.error('Failed to retrieve invoice export state:', error);
      showToast({
        title: 'Error',
        message: 'Failed to retrieve invoice export status:' + (error instanceof Error ? error.message : ''),
        type: 'error',
      });
    }
  }

  function showUploadTestFileModal() {
    modalStore.trigger({
      type: 'prompt',
      title: 'Upload Test File',
      body: 'Enter a filename for the test file to upload to SharePoint.<br>This ".txt" file will be uploaded to the clients SharePoint folder.',
      value: 'test',
      valueAttr: { type: 'text', required: true, maxlength: 255 },
      response: (fileName: string | false) => {
        if (fileName) {
          uploadFileTest(fileName);
        }
      },
    });
  }

  async function uploadFileTest(fileName: string) {
    try {
      const response = await fetchWithAuth(`/api/export/sharepoint-upload-test-file?clientId=${clientId}&fileName=${fileName}`);

      const result = await response.json();

      if (result.success) {
        showToast({
          title: 'Success',
          message: `Test file (${fileName}.txt) uploaded successfully`,
          type: 'success',
        });

      } else {
        console.error('Failed to upload test file:', result.error);
        // Show modal with error
        showToast({
          title: 'Error',
          message: 'Failed to upload test file:' + result.error,
          type: 'error',
        });
      }
    } catch (error) {
      console.error('Failed to upload test file:', error);
      showToast({
        title: 'Error',
        message: 'Failed to upload test file:' + (error instanceof Error ? error.message : ''),
        type: 'error',
      });
    }
  }

</script>

<div class="p-5">
  <h3 class="mb-4 text-xl font-semibold">Sharepoint Export Setup</h3>
  <p class="mb-4 text-sm text-gray-600">
      Configure and test Sharepoint export for this client with API credentials and sync settings.
      <br />
      First setup the API configuration, then upload the Certificate file.
      Once configured, use Show Column Configuration or Show Recent Files to check access to SharePoint.
      You can use Upload Test File to load a file into the clients SharePoint.
  </p>

  <div class="space-y-6 rounded-lg border border-gray-200 bg-gray-50 p-4">
    <div class="flex items-center justify-between">
      <h4 class="font-semibold">Sharepoint API Configuration</h4>
      {#if isLoading}
        <span class="text-sm text-gray-500">Loading existing configuration...</span>
      {:else if hasExistingConfig}
        <div class="flex items-center gap-2 rounded-md bg-blue-100 px-3 py-1 text-sm text-blue-700">
          <Info size={16} />
          <span>Updating existing configuration</span>
        </div>
      {:else}
        <div class="flex items-center gap-2 rounded-md bg-orange-100 px-3 py-1 text-sm text-orange-700">
          <Info size={16} />
          <span>Creating client configuration</span>
        </div>
      {/if}
    </div>

    <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
      <div>
        <label for="site-url" class="mb-2 flex items-center gap-2 text-sm font-medium">
          Site Url
          <RequiredBadge />
        </label>
        <input
          id="site-url"
          type="text"
          bind:value={siteUrl}
          placeholder="example.sharepoint.com"
          class="w-full rounded-md border px-3 py-2 transition-all focus:outline-none focus:ring-2 focus:ring-primary-500"
          class:border-gray-300={siteUrl}
          class:border-red-300={!siteUrl}
          disabled={isExecuting}
        />
      </div>
      <div>
        <label for="client-id" class="mb-2 flex items-center gap-2 text-sm font-medium">
          Client Id
          <RequiredBadge />
        </label>
        <div class="relative">
          {#if showClientId}
            <input
              id="client-id"
              type="text"
              bind:value={sharepointClientId}
              placeholder="Enter Client Id"
              maxlength="100"
              class="w-full rounded-md border px-3 py-2 pr-10 transition-all focus:outline-none focus:ring-2 focus:ring-primary-500"
              class:border-gray-300={sharepointClientId}
              class:border-red-300={!sharepointClientId}
              disabled={isExecuting}
            />
          {:else}
            <input
              id="client-id"
              type="password"
              bind:value={sharepointClientId}
              placeholder="Enter Client Id"
              maxlength="100"
              class="w-full rounded-md border px-3 py-2 pr-10 transition-all focus:outline-none focus:ring-2 focus:ring-primary-500"
              class:border-gray-300={sharepointClientId}
              class:border-red-300={!sharepointClientId}
              disabled={isExecuting}
            />
          {/if}
          <button
            type="button"
            class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
            on:click={() => (showClientId = !showClientId)}
            disabled={isExecuting}
          >
            {#if showClientId}
              <EyeSlash size={20} />
            {:else}
              <Eye size={20} />
            {/if}
          </button>
        </div>
      </div>
    </div>

    <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
      <div>
        <label for="folder-location" class="mb-2 flex items-center gap-2 text-sm font-medium">
          Folder Location
          <RequiredBadge />
        </label>
        <input
          id="folder-location"
          type="text"
          bind:value={folderLocation}
          placeholder="sites/example"
          class="w-full rounded-md border px-3 py-2 transition-all focus:outline-none focus:ring-2 focus:ring-primary-500"
          class:border-gray-300={folderLocation}
          class:border-red-300={!folderLocation}
          disabled={isExecuting}
        />
      </div>
      <div>
        <label for="tenant-id" class="mb-2 flex items-center gap-2 text-sm font-medium">
          Tenant Id
          <RequiredBadge />
        </label>
        <div class="relative">
          {#if showTenantId}
            <input
              id="tenant-id"
              type="text"
              bind:value={tenantId}
              placeholder="Enter Tenant Id"
              maxlength="100"
              class="w-full rounded-md border px-3 py-2 pr-10 transition-all focus:outline-none focus:ring-2 focus:ring-primary-500"
              class:border-gray-300={tenantId}
              class:border-red-300={!tenantId}
              disabled={isExecuting}
            />
          {:else}
            <input
              id="tenant-id"
              type="password"
              bind:value={tenantId}
              placeholder="Enter Tenant Id"
              maxlength="100"
              class="w-full rounded-md border px-3 py-2 pr-10 transition-all focus:outline-none focus:ring-2 focus:ring-primary-500"
              class:border-gray-300={tenantId}
              class:border-red-300={!tenantId}
              disabled={isExecuting}
            />
          {/if}
          <button
            type="button"
            class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
            on:click={() => (showTenantId = !showTenantId)}
            disabled={isExecuting}
          >
            {#if showTenantId}
              <EyeSlash size={20} />
            {:else}
              <Eye size={20} />
            {/if}
          </button>
        </div>
      </div>
    </div>

    <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
      <div>
        <label for="folder-url" class="mb-2 flex items-center gap-2 text-sm font-medium">
          Folder Url
          <RequiredBadge />
        </label>
        <input
          id="folder-url"
          type="text"
          bind:value={folderUrl}
          placeholder="example"
          class="w-full rounded-md border px-3 py-2 transition-all focus:outline-none focus:ring-2 focus:ring-primary-500"
          class:border-gray-300={folderUrl}
          class:border-red-300={!folderUrl}
          disabled={isExecuting}
        />
      </div>
      <div>
        <label for="has-certificate-file" class="mb-2 flex items-center gap-2 text-sm font-medium">
          Certificate
        </label>
        <input
          id="has-certificate-file"
          type="text"
          bind:value={hasCertificateText}
          placeholder="Certificate file is not loaded"
          maxlength="50"
          class="w-full rounded-md border px-3 py-2 transition-all focus:outline-none bg-gray-50 text-gray-700"
          class:border-gray-300={hasCertificate}
          class:border-red-300={!hasCertificate}
          readonly
        />
      </div>
    </div>

  </div>


  <div class="mt-6">
    <div class="flex items-center justify-between gap-3">
      <div class="flex gap-3">
        <Button label="Invoice Export Status" onClick={getExportStatus} disabled={isExecuting} colour="secondary" />
        {#if hasExistingConfig && hasCertificate}
          <Button label="Show Column Configuration" onClick={retrieveMetaData} disabled={isExecuting} colour="secondary" />
          <Button label="Show Recent Files" onClick={retrieveFileList} disabled={isExecuting} colour="secondary" />
          <Button label="Upload Test File" onClick={showUploadTestFileModal} disabled={isExecuting} colour="secondary" />
        {/if}
      </div>
      <div class="flex gap-3">
        <Button label="Clear Form" onClick={clearForm} disabled={isExecuting} colour="secondary" />
        <Button label="Upload Certificate" onClick={showCertificateModal} disabled={isExecuting || !hasExistingConfig} colour="secondary" />
        <Button 
          label={isExecuting ? 'Saving...' : hasExistingConfig ? 'Update Configuration' : 'Save Configuration'} 
          onClick={saveSharepointConfiguration} 
          disabled={isExecuting || !canSave} 
          colour="primary" 
        />
      </div>
    </div>
  </div>

</div>
